/* eslint-disable jsx-a11y/label-has-associated-control */
import {
  Dialog,
  Grid,
  IconButton,
  makeStyles,
  Typography,
} from '@material-ui/core';
import ReplayIcon from '@material-ui/icons/Replay';
import { connect, ConnectedProps } from 'react-redux';

import { colors } from 'design-system';
import { XCircle } from 'react-feather';

import { compressImage } from 'src/functions/compressImage';
import isCaptureSupported from 'src/functions/isCaptureSupported';
import {
  registerCarImage,
  setUploadStateMap,
} from '../../../../../store/inspectionForm/actions';

// hooks
import usePhotoCapturer from '../../../../PhotoCapturer/usePhotoCapturer';

export enum SonarStates {
  Active,
  Completed,
  Disabled,
}

const useStyles = makeStyles({
  iconButton: {
    position: 'absolute',
    top: 20,
    right: 20,
    color: colors.white,
    zIndex: 10,
  },
  titleImage: {
    position: 'absolute',
    top: '150px',
    textAlign: 'center',
  },
  informationQuestionGrid: {
    position: 'absolute',
    bottom: '250px',
    textAlign: 'center',
  },
  informationQuestionText: {
    color: colors.white,
    width: '70%',
    margin: 'auto',
  },
  imagePreview: () => ({
    position: 'absolute',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'contain',
    backgroundPosition: 'center',
    width: '100%',
    height: '70%',
  }),
  retakePhotoButton: {
    color: colors.white,
    backgroundColor: colors.bright_turquoise,
    padding: '5px',
    top: 'auto',
    width: '64px',
    bottom: '145px',
    height: '64px',
    position: 'fixed',
    left: '50%',
    marginLeft: -32,
  },
});

const nameMap: { [key: string]: string } = {
  frontal: 'Frontal',
  back: 'Trasera',
  interior: 'Interior',
  left: 'Izquierda',
  right: 'Derecha',
};

export const PictureDetail = ({
  open,
  imageId,
  base64Image,
  onCaptureCancelled,
  registerCarImageAction,
  setUploadStateMapAction,
}: Props): JSX.Element => {
  const classes = useStyles();
  const captureSupport = isCaptureSupported();
  function onPhotoCaptured(identifier: string, base64img: string): void {
    registerCarImageAction(identifier, base64img);
    setUploadStateMapAction({ [identifier]: 'initial' });
    onCaptureCancelled();
  }

  const { cameraView, requestImage } = usePhotoCapturer(onPhotoCaptured);

  /**
   * Returns a valid display name for the given key
   *
   * @param name - The unique key that identifies the photo
   */
  function nameForKey(key: string): string {
    if (Object.keys(nameMap).includes(key)) {
      return nameMap[key];
    }
    return '';
  }

  return (
    <>
      {cameraView}
      <Dialog
        id="sales-web-inspection-div-picturedetail"
        data-testid="picturedetaildialog"
        fullScreen
        open={open}
        PaperProps={{
          style: {
            backgroundColor: colors.blue_zodiac,
          },
        }}
      >
        <IconButton
          id="sales-web-inspection-button-closedetail"
          data-testid="close-detail-button"
          onClick={onCaptureCancelled}
          className={classes.iconButton}
        >
          <XCircle color={colors.white} />
        </IconButton>
        <Grid
          container
          data-testid="footer"
          justifyContent="center"
          className={classes.titleImage}
        >
          <Grid xs={12} item container alignItems="center">
            <Typography
              variant="caption"
              data-testid="image-title"
              style={{ color: colors.white, margin: 'auto' }}
            >
              {nameForKey(imageId)}
            </Typography>
          </Grid>
        </Grid>
        {base64Image !== '' && (
          <>
            <div
              data-testid="image-detail"
              className={classes.imagePreview}
              style={{
                backgroundImage: `url(${base64Image})`,
              }}
            />
          </>
        )}

        <Grid
          container
          data-testid="footer"
          justifyContent="center"
          className={classes.informationQuestionGrid}
        >
          <Grid xs={12} item container alignItems="center">
            <Typography
              variant="body1"
              data-testid="information-question"
              className={classes.informationQuestionText}
            >
              ¿Quieres mejorarla? puedes volver a tomarla y reemplazarla.
            </Typography>
          </Grid>
        </Grid>
        {captureSupport && (
          <input
            accept="image/*"
            style={{ display: 'none' }}
            id={`${imageId}-list-camera`}
            data-testid={`${imageId}-list-camera`}
            type="file"
            capture="environment"
            onChange={async (e) => {
              if (!e.target.files) return;
              const compressedImage = await compressImage(e.target.files[0]);
              onPhotoCaptured(imageId, compressedImage);
            }}
          />
        )}
        <label
          htmlFor={`${imageId}-list-camera`}
          style={{
            padding: '5px',
            top: 'auto',
            width: '64px',
            bottom: '145px',
            height: '64px',
            position: 'fixed',
            left: '50%',
            marginLeft: -32,
          }}
        >
          <IconButton
            id="sales-web-inspection-button-retakephoto"
            data-testid="button-retakephoto"
            className={classes.retakePhotoButton}
            onClick={(): void => requestImage(imageId)}
            style={{ pointerEvents: captureSupport ? 'none' : 'auto' }}
          >
            <ReplayIcon />
          </IconButton>
        </label>
      </Dialog>
    </>
  );
};

const reduxActions = {
  registerCarImageAction: registerCarImage,
  setUploadStateMapAction: setUploadStateMap,
};
const connector = connect(null, reduxActions);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface Props extends PropsFromRedux {
  open: boolean;
  imageId: string;
  base64Image: string;
  onCaptureCancelled: () => void;
}

export default connector(PictureDetail);
